import React from 'react';
import PageButton from '../../elements/PageButton/PageButton';
import {PlayerIcon} from '../../coloredIcons/PlayerIcon/PlayerIcon';
import VisibleBotMark from '../../VisibleBotMark/VisibleBotMark';
import {checkIfBotMarkShown} from '../../../utils/bots/checkIfBotMarkShown';
import {AI_MATCH_SHORT_TITLE} from '../../../configs/aiMatch';
import {PLAYER_BONUS_LABELS, PLAYER_BONUS_TYPES} from '../../../configs/playerBonus';
import {getLobbyBonusType} from '../../../utils/lobby/lobbyBonus';
import LobbyListTypeIcons from '../LobbyListTypeIcons';
import {getLobbyItemButtonId} from '../lobbyListItems';
import {
  checkIfLobbyListSeatsOpen,
  getLobbyListMaxPlayers,
  getLobbyListPhase,
  getLobbyListSeatsCount,
  getLobbyListStatusText,
  LOBBY_LIST_PHASES,
} from '../lobbyListStatus';

/**
 * Что сулит нажатие на карточку: в открытый состав со свободным местом —
 * войти; в идущий матч и в ИИ-лобби, где мест нет или начались баны, —
 * смотреть (сервер пустит зрителем, см. planAiPromoJoin). В полное обычное
 * лобби вход закрыт (ботов ради вошедшего не вытесняют), а в рейтинговое,
 * чей состав собирает сервер (матч-мейкинг, витрина ботов), из списка не
 * войти — подписи нет, этап и «6 / 6» видны на карточке
 */
const getActionLabel = (lobby, phase) => {
  if (phase === LOBBY_LIST_PHASES.STARTED) {
    return 'Смотреть';
  }
  if (lobby.isAiMatch) {
    const isFull = (lobby.players || []).length >= getLobbyListMaxPlayers(lobby);
    return phase === LOBBY_LIST_PHASES.GATHERING && !isFull ? 'Войти' : 'Смотреть';
  }
  if (!checkIfLobbyListSeatsOpen(lobby)) {
    return null;
  }
  const playerCount = lobby.playerCount ?? (lobby.players || []).length;
  return playerCount < getLobbyListMaxPlayers(lobby) ? 'Войти' : null;
};

/**
 * Места лобби в сборе: занятые — ник (у «явного» бота и ИИ — их значок),
 * свободные — пустые. Фракции не показываются: выбор соперника до старта
 * матча тайна (как в самом лобби)
 */
const LobbyListCardSeats = ({lobby}) => {
  const players = lobby.players || [];
  const seatsCount = getLobbyListSeatsCount(lobby);
  return (
    <span className="lobby-list-card_seats">
      {Array.from({length: seatsCount}, (_, index) => {
        const player = players[index];
        if (!player) {
          return <span key={`free-${index}`} className="lobby-list-card_seat lobby-list-card_seat_free">Свободно</span>;
        }
        return (
          <span key={player.id || index} className="lobby-list-card_seat" title={player.name}>
            <span className="lobby-list-card_seat-name">{player.name}</span>
            {checkIfBotMarkShown(player) && <VisibleBotMark player={player} className="lobby-list-card_seat-mark"/>}
          </span>
        );
      })}
    </span>
  );
};

/**
 * Карточка лобби в десктопном списке (LobbyListDesktop). Лобби в сборе —
 * крупная: места с никами, этап, бонус. Идущий матч — строка: ники игроков
 * не показываются вовсе — у фейкового приватного лобби их нет, и по ним его
 * можно было бы отличить от настоящего.
 *
 * Вся карточка — одна кнопка (PageButton с тем же id аналитики, что у плашки
 * телефона, см. getLobbyItemButtonId)
 *
 * @param {Object} props
 * @param {Object} props.lobby - дескриптор лобби из списка
 * @param {number} props.now - время сервера, мс (для отсчетов этапа)
 * @param {boolean} props.isGuest
 * @param {boolean} props.isViewerAdmin
 * @param {boolean} props.isAdmin
 * @param {function(Object): void} props.onClick
 */
const LobbyListCard = ({lobby, now, isGuest, isViewerAdmin, isAdmin, onClick}) => {
  const phase = getLobbyListPhase(lobby);
  const isStarted = phase === LOBBY_LIST_PHASES.STARTED;
  const playerCount = lobby.playerCount ?? (lobby.players || []).length;
  // В открытом составе — «занято / всего мест» (и у полного лобби: «6 / 6»)
  const countText = checkIfLobbyListSeatsOpen(lobby)
    ? `${playerCount} / ${getLobbyListMaxPlayers(lobby)}`
    : playerCount;
  const bonusType = getLobbyBonusType(lobby);
  const actionLabel = getActionLabel(lobby, phase);
  const statusText = getLobbyListStatusText(lobby, now);
  const title = lobby.isAiMatch ? AI_MATCH_SHORT_TITLE : `Лобби ${lobby.lobbyName}`;

  return (
    <PageButton
      className={[
        'lobby-list-card',
        isStarted ? 'lobby-list-card_started' : 'lobby-list-card_gathering',
        lobby.isAiMatch ? 'lobby-list-card_ai' : '',
      ].filter(Boolean).join(' ')}
      onClick={() => onClick(lobby)}
      pageId="LobbyListPage"
      buttonId={getLobbyItemButtonId(lobby)}
      isGuest={isGuest}
      data-lobby-id={lobby.lobbyId}
    >
      <span className="lobby-list-card_header">
        <LobbyListTypeIcons lobby={lobby} isViewerAdmin={isViewerAdmin} isAdmin={isAdmin}/>
        {/* У ИИ-лобби «AI» показывает иконка слева — в названии его нет.
            Длинное название обрезается — целиком оно в подсказке */}
        <span className="lobby-list-card_name" title={title}>{title}</span>
        <span className="lobby-list-card_count" title="Игроки">
          {countText}
          <PlayerIcon className="lobby-list-card_count-icon" color="var(--text-primary-color)" isSmall/>
        </span>
      </span>

      {!isStarted && <LobbyListCardSeats lobby={lobby}/>}

      <span className="lobby-list-card_footer">
        {statusText && (
          <span className={`lobby-list-card_status lobby-list-card_status_${phase}`}>{statusText}</span>
        )}
        {/* Только у лобби в сборе: у фейкового приватного лобби бонуса не
            бывает, и на строке идущего матча он выдавал бы настоящий */}
        {!isStarted && bonusType !== PLAYER_BONUS_TYPES.NONE && (
          <span className="lobby-list-card_bonus">Бонус: {PLAYER_BONUS_LABELS[bonusType]}</span>
        )}
        {actionLabel && <span className="lobby-list-card_action">{actionLabel}</span>}
      </span>
    </PageButton>
  );
};

export default LobbyListCard;
